/**
 * Warstwa API: MATERIAŁY PROCEDURY — PDF, dokumenty Word i zrzuty ekranu zachowane po rozczytaniu (polecenie właściciela 2026-10-02).
 *
 * Właściciel: „dodaj opcję OCR do rozczytywania screenów oraz tekstów oraz zapisuj zrzuty po rozczytaniu, by dało się z tego zrobić
 * bibliotekę procedur multimedialną nawet dla plików; to bardzo ważne, by móc tutaj rozczytać pdf albo wrzucić wordy i screeny”.
 *
 * Trasy backendu (PolitykiController.Materialy.cs; zapis za bramką redakcji dokumentu, odczyt dwupoziomowy — adresat obowiązującej
 * wersji pobiera wyłącznie obraz użyty w jej treści):
 *  - `POST api/polityki/{id}/wersje/{versionId}/import-materialow` (multipart `pliki`, `tryb`=dopisz|zastap, `ocr`=true|false);
 *  - `GET api/polityki/{id}/materialy?typ=` — lista; `GET …/materialy/{mid}` — plik (blob); `GET …/{mid}/podglad-pdf` (503 = brak Gotenberga);
 *  - `GET …/{mid}/tekst` — `{ tekst }`; `PUT …/{mid}` `{ opis, kolejnosc }`; `POST …/{mid}/wycofaj` (409 = w zatwierdzonej wersji);
 *  - `POST …/{mid}/ocr` `{ versionId, tryb }` — ponowne rozczytanie;
 *  - `GET api/polityki/biblioteka-materialow?typ=&companyId=&obszar=&szukaj=&strona=` — biblioteka materiałów (403 = brak wejścia).
 *
 * Stary `apiService.importPolitykaOcr` (apiService.js) ZOSTAJE — serwer deleguje go do tej samej ścieżki.
 * Kontrakt: `attach(api)` dostaje instancję axios z apiService.js i zwraca nazwane metody (nazwy unikalne — patrz attachApiModules).
 */

/** Rozszerzenia przyjmowane przez import — lustro `SygnaturyPlikow.Rozszerzenia` (atrybut `accept` pola wyboru plików). */
export const ROZSZERZENIA_MATERIALOW = Object.freeze([".pdf", ".docx", ".png", ".jpg", ".jpeg", ".tif", ".tiff", ".bmp", ".webp"]);
export const LIMIT_PLIKU_MATERIALU = 25 * 1024 * 1024;
export const LIMIT_PLIKOW_NA_IMPORT = 20;
/**
 * Suma plików JEDNEGO żądania — lustro `SygnaturyPlikow.MaksRozmiarZadania` (90 MB). nginx witryny przepuszcza 100 MB ciała żądania,
 * więc większy import idzie kolejnymi partiami (`importMaterialow` dzieli sam — patrz `podzielNaPartie`).
 */
export const LIMIT_ZADANIA_MATERIALOW = 90 * 1024 * 1024;

/**
 * Podział plików na partie wysyłane kolejnymi żądaniami: każda ≤ `limitBajtow` i ≤ `limitPlikow` plików, kolejność zachowana.
 * Plik większy od limitu partii (serwer i tak odrzuci go limitem 25 MB na plik) idzie sam.
 */
export function podzielNaPartie(pliki, { limitBajtow = LIMIT_ZADANIA_MATERIALOW, limitPlikow = LIMIT_PLIKOW_NA_IMPORT } = {}) {
    const partie = [];
    let biezaca = [];
    let suma = 0;
    for (const p of pliki || []) {
        const rozmiar = Number(p && p.size) || 0;
        if (biezaca.length > 0 && (suma + rozmiar > limitBajtow || biezaca.length >= limitPlikow)) {
            partie.push(biezaca);
            biezaca = [];
            suma = 0;
        }
        biezaca.push(p);
        suma += rozmiar;
    }
    if (biezaca.length > 0) partie.push(biezaca);
    return partie;
}

const rozmiarPartii = (partia) => partia.reduce((s, p) => s + (Number(p && p.size) || 0), 0);

/** Wynik kolejnych partii scalony w jeden — materiały i statusy plików w kolejności wysyłki, treść (kreatorJson/html) z ostatniej partii. */
const scalWyniki = (a, b) => {
    const dane = b || {};
    if (!a) return { ...dane, materialy: [...(dane.materialy || [])], pliki: [...(dane.pliki || [])] };
    return {
        ...dane,
        materialy: [...a.materialy, ...(dane.materialy || [])],
        pliki: [...a.pliki, ...(dane.pliki || [])],
        stron: (Number(a.stron) || 0) + (Number(dane.stron) || 0),
        kreatorJson: dane.kreatorJson ?? a.kreatorJson,
        html: dane.html ?? a.html,
        odciskPo: dane.odciskPo ?? a.odciskPo,
    };
};

/**
 * [domknięcie ea 2026-10-04, krytyk: „po 400 «rozczytany tekst jest za długi» lista plików zostaje pełna, a «Wczytaj» czynne — ponowne
 * kliknięcie robi drugie kopie materiałów”] Odmowa zapisu TREŚCI po zapisie MATERIAŁÓW niesie w ciele `pliki` (statusy) i `materialy` —
 * te pliki są już w „Materiałach procedury”, więc trafiają do `czesciowyWynik` jak partie, które przeszły (ekran zdejmuje je z listy).
 */
const zapisaneMimoOdmowy = (err) => {
    const dane = err && err.response && err.response.data;
    return dane && Array.isArray(dane.pliki) && dane.pliki.length ? dane : null;
};

const wyslijPartie = (api, id, versionId, pliki, tryb, ocr, onPostep) => {
    const form = new FormData();
    for (const p of pliki || []) form.append("pliki", p, p.name);
    form.append("tryb", tryb);
    form.append("ocr", ocr ? "true" : "false");
    return api.post(`/polityki/${id}/wersje/${versionId}/import-materialow`, form, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 0,
        onUploadProgress: (e) => {
            if (typeof onPostep === "function" && e && e.total) onPostep(Math.round((e.loaded / e.total) * 100));
        },
    });
};

export function attach(api) {
    return {
        /**
         * Import wielu plików do wersji roboczej. Kolejność `pliki` = kolejność na liście (tak trafiają do FormData).
         * Pliki razem ponad `LIMIT_ZADANIA_MATERIALOW` (albo ponad `LIMIT_PLIKOW_NA_IMPORT`) idą KOLEJNYMI żądaniami: pierwsza partia z
         * wybranym trybem, następne „dopisz”; wynik scalony (jak z jednego żądania). Błąd partii po udanych wcześniejszych: odrzucenie
         * z `err.czesciowyWynik` — to, co już zapisano (materiały i statusy plików), żeby ekran powiedział, co weszło.
         * @param {number} id dokument
         * @param {number} versionId wersja robocza
         * @param {File[]} pliki
         * @param {{ tryb?: "dopisz"|"zastap", ocr?: boolean }} [opcje]
         * @param {(procent:number)=>void} [onPostep] postęp wysyłania (0–100, łącznie dla wszystkich partii)
         * @returns `{ materialy, pliki:[{ nazwa, status, stron, blokow, komunikat }], kreatorJson, versionId, html, odciskPo, stron }`
         */
        importMaterialow: async (id, versionId, pliki, { tryb = "dopisz", ocr = true } = {}, onPostep) => {
            const partie = podzielNaPartie(pliki);
            if (partie.length <= 1) {
                try {
                    return await wyslijPartie(api, id, versionId, partie[0] || [], tryb, ocr, onPostep);
                } catch (err) {
                    const zapisane = zapisaneMimoOdmowy(err);
                    if (err && zapisane) err.czesciowyWynik = scalWyniki(null, zapisane);
                    throw err;
                }
            }
            const razem = partie.reduce((s, p) => s + rozmiarPartii(p), 0) || 1;
            let wyslane = 0;
            let scalone = null;
            let odpowiedz = null;
            /* [domknięcie ea 2026-10-04, krytyk: „import partiami gubi zamiar «Zastąp»”] „Zastąp” idzie do KOLEJNYCH partii, dopóki któraś
               nie zapisze treści (serwer oddaje `html`). Partia bez tekstu (same zrzuty, błąd OCR) treści nie zapisuje — z dawnym
               „pierwsza zastąp, reszta dopisz” druga partia dopisywała się do STAREJ kartki, choć człowiek wybrał „Zastąp obecną treść”. */
            let trybPartii = tryb;
            for (let i = 0; i < partie.length; i++) {
                const rozmiar = rozmiarPartii(partie[i]);
                // [integracja dz+ea+eb 2026-10-04] Stan przed tą partią jako stała pętli (no-loop-func w buildzie) — to samo
                // co dotąd: `wyslane` rośnie dopiero po zakończeniu partii, a postęp jest wołany w jej trakcie.
                const przedPartia = wyslane;
                const postep = typeof onPostep === "function"
                    ? (procent) => onPostep(Math.min(100, Math.round(((przedPartia + (rozmiar * procent) / 100) / razem) * 100)))
                    : undefined;
                try {
                    odpowiedz = await wyslijPartie(api, id, versionId, partie[i], trybPartii, ocr, postep);
                } catch (err) {
                    const zapisane = zapisaneMimoOdmowy(err);
                    const czesciowy = zapisane ? scalWyniki(scalone, zapisane) : scalone;
                    if (err && czesciowy) err.czesciowyWynik = czesciowy;
                    throw err;
                }
                wyslane += rozmiar;
                scalone = scalWyniki(scalone, odpowiedz && odpowiedz.data);
                if (odpowiedz && odpowiedz.data && odpowiedz.data.html != null) trybPartii = "dopisz";
            }
            return { ...odpowiedz, data: scalone };
        },

        /** Lista materiałów dokumentu (`typ`: obraz / pdf / docx / puste). */
        listaMaterialow: (id, { typ } = {}) => api.get(`/polityki/${id}/materialy`, { params: typ ? { typ } : {} }),

        /** Plik materiału jako Blob (podgląd przez ObjectURL — adres API wymaga JWT). */
        blobMaterialu: (id, mid) => api.get(`/polityki/${id}/materialy/${mid}`, { responseType: "blob" }),

        /** Podgląd PDF (DOCX przez Gotenberg); 503 = komunikat serwera + „Pobierz oryginał”. */
        podgladPdfMaterialu: (id, mid) => api.get(`/polityki/${id}/materialy/${mid}/podglad-pdf`, { responseType: "blob" }),

        /** Tekst rozczytany — `{ tekst }`. */
        tekstMaterialu: (id, mid) => api.get(`/polityki/${id}/materialy/${mid}/tekst`),

        /** Opis i kolejność. */
        zmienMaterial: (id, mid, { opis, kolejnosc } = {}) => api.put(`/polityki/${id}/materialy/${mid}`, { opis, kolejnosc }),

        /** Wycofanie (409 = materiał w zatwierdzonej wersji — komunikat serwera). */
        wycofajMaterial: (id, mid) => api.post(`/polityki/${id}/materialy/${mid}/wycofaj`),

        /** Ponowne rozczytanie materiału i dopisanie bloków do wersji roboczej. */
        ocrMaterialu: (id, mid, { versionId, tryb = "dopisz" }) => api.post(`/polityki/${id}/materialy/${mid}/ocr`, { versionId, tryb }, { timeout: 0 }),

        /** Biblioteka materiałów: `{ pozycje, razem, strona, naStronie }`; 403 = brak wejścia (chip „Materiały” ukryty). */
        bibliotekaMaterialow: ({ typ, companyId, obszar, szukaj, strona = 1 } = {}) =>
            api.get("/polityki/biblioteka-materialow", { params: { typ, companyId, obszar, szukaj, strona } }),
    };
}
